import ActionTextIcon from "@/components/icons/ActionTextIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleBruttoPoints, formatLocaleCurrency, formatLocaleDate } from "@/utils/formatUtils";
import { sortableColumnProps } from "@/utils/tableUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Alert, Card, Col, Row, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import { useFilterContracts, useToggleVerificationExportInclusion } from "../../../../../common/api/queries";
import { TableSizes } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { formatAgentName } from "../../../../agent/utils";
import { useCurrentAgentAccount, useHasPermissions } from "../../../../auth/hooks";
import { ClientTypeTagWithName } from "../../../../client/components/tags/ClientTypeTagWithName";
import { ContractOrderByColumn, ContractVerificationStatus, ContractView } from "../../../enums";
import { CONTRACT_ROUTE_PATHS } from "../../../paths";
import { ContractFilterPageRequest, ContractList } from "../../../types";
import {
  canSendContractToBackoffice,
  handleTableChange,
  hasVerificationExportTogglePermissions,
  VERIFICATION_STATUSES_FOR_SENDING_TO_BACKOFFICE,
} from "../../../utils";
import ContractChangeVerificationStatusForm from "../../forms/ContractChangeVerificationStatusForm";
import { ContractOriginTag } from "../../tags/ContractOriginTag";
import ContractStatusTag from "../../tags/ContractStatusTag";
import ContractVerificationStatusTag from "../../tags/ContractVerificationStatusTag";

type Props = {
  validatedSearchParams?: ContractFilterPageRequest;
  updateSearchParams: (searchParams: ContractFilterPageRequest, append?: boolean, replace?: boolean) => void;
};

export const ContractPredictedCommissionsListTableView = ({ validatedSearchParams, updateSearchParams }: Props) => {
  const { t } = useTranslation();

  const userAccount = useCurrentAgentAccount();
  const hasCommissionsPermission = useHasPermissions(Permission.COMMISSIONS);

  const [contractForVerificationForm, setContractForVerificationForm] = useState<ContractList>();

  const { data: contractListData, isFetching } = useFilterContracts(
    validatedSearchParams
      ? { ...validatedSearchParams, report: ContractView.INTERNAL_PREDICTED_COMMISSIONS_REPORT }
      : undefined,
  );
  const toggleVerificationExportInclusion = useToggleVerificationExportInclusion();

  const columns = useMemo(() => {
    const items: ColumnsType<ContractList> = [
      {
        key: "effectiveBeginningOrSignDate",
        title: (
          <>
            {t("contract.attrs.effectiveBeginningDateShort")}
            <br />
            {t("contract.attrs.signDate")}
          </>
        ),
        width: 180,
        dataIndex: ContractOrderByColumn.EFFECTIVE_BEGINNING_OR_SIGN_DATE,
        ...sortableColumnProps(ContractOrderByColumn.EFFECTIVE_BEGINNING_OR_SIGN_DATE, contractListData),
        render: (_, record) => {
          const date = record.effectiveBeginningDate || record.signDate;
          return date ? formatLocaleDate(date) : undefined;
        },
      },
      {
        key: "contractNumbers",
        title: t("contract.attrs.contractNumber"),
        width: 170,
        render: (_, record) => (
          <>
            {record.contractNumber && (
              <>
                <Ellipsis tooltip={record.contractNumber}>
                  <Link className="no-link-color" to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.id })}>
                    {record.contractNumber}
                  </Link>
                </Ellipsis>
                <br />
              </>
            )}
            {record.secondaryContractNumber && (
              <Ellipsis tooltip={record.secondaryContractNumber}>
                <Link className="no-link-color" to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.id })}>
                  {record.secondaryContractNumber}
                </Link>
              </Ellipsis>
            )}
          </>
        ),
      },
      {
        key: "client",
        title: t("contract.attrs.client"),
        width: 260,
        render: (_, record) =>
          record.policyHolder && <ClientTypeTagWithName client={record.policyHolder} ellipsis showLink />,
      },
      {
        key: "institution",
        title: t("contract.attrs.institution"),
        width: 190,
        render: (_, record) => <Ellipsis>{record.institution.name}</Ellipsis>,
      },
      {
        key: "product",
        title: t("contract.attrs.product"),
        width: 240,
        render: (_, record) => <Ellipsis>{record.product.name}</Ellipsis>,
      },
      {
        key: "predictedCommissions",
        title: (
          <>
            <Row>
              <Col span={24} className="center-align">
                {t("contract.attrs.predictedCommissions._label")}
              </Col>
            </Row>
            <Row>
              <Col span={12}>{t("contract.attrs.predictedCommissions.gainerIds")}</Col>
              <Col span={6} className="right-align">
                {t("contract.attrs.predictedCommissions.commissionAmount")}
              </Col>
              <Col span={6} className="right-align">
                {t("contract.attrs.predictedCommissions.bruttoPointAmount")}
              </Col>
            </Row>
          </>
        ),
        width: 550,
        render: (_, record) =>
          record.predictedCommission?.canBeCalculated &&
          (hasCommissionsPermission || record.predictedCommission.calculatedWithoutErrors) &&
          record.predictedCommission.calculatedCommissions
            .filter((pcc) => pcc.individualBruttoPoints)
            .map((pcc) => (
              <Row key={pcc.id}>
                <Col span={12} className="predicted-commissions-data-cell">
                  <Ellipsis>{formatAgentName(pcc.gainer)}</Ellipsis>
                </Col>
                {pcc.errors.length ? (
                  <Col span={12} className="predicted-commissions-data-cell">
                    <Alert
                      type="error"
                      showIcon
                      title={<Ellipsis>{pcc.errors.flatMap((e) => e.description).join(" ")}</Ellipsis>}
                    />
                  </Col>
                ) : (
                  <>
                    <Col span={6} className="predicted-commissions-data-cell right-align">
                      {isDefined(pcc.commissionAmount) ? formatLocaleCurrency(pcc.commissionAmount) : undefined}
                    </Col>
                    <Col span={6} className="predicted-commissions-data-cell right-align">
                      {isDefined(pcc.bruttoPointAmount) ? formatLocaleBruttoPoints(pcc.bruttoPointAmount) : undefined}
                    </Col>
                  </>
                )}
              </Row>
            )),
      },
      {
        key: "status",
        title: (
          <>
            {t("contract.enums.status._label")}
            <br />
            {t("contract.enums.verificationStatus._label")}
          </>
        ),
        width: 210,
        align: "center",
        render: (_, record) =>
          record.status && record.verificationStatus ? (
            <>
              <ContractStatusTag status={record.status} style={{ marginRight: 0 }} />
              <br />
              <ContractVerificationStatusTag
                status={record.verificationStatus}
                note={record.verificationNote}
                includedInExport={record.includeInVerificationExport}
                style={{ marginTop: 6, marginRight: 0 }}
                onSendClick={
                  VERIFICATION_STATUSES_FOR_SENDING_TO_BACKOFFICE.includes(record.verificationStatus) &&
                  userAccount &&
                  canSendContractToBackoffice(userAccount, record)
                    ? () => setContractForVerificationForm(record)
                    : undefined
                }
                onExportToggleClick={
                  record.verificationStatus === ContractVerificationStatus.SENT &&
                  userAccount &&
                  hasVerificationExportTogglePermissions(record.type, userAccount.permissions)
                    ? () =>
                        toggleVerificationExportInclusion.mutate({
                          id: record.id,
                          object: { optimisticLockVersion: record.optimisticLockVersion as number },
                        })
                    : undefined
                }
              />
            </>
          ) : undefined,
      },
      {
        key: "origin",
        align: "center",
        fixed: "right",
        width: 60,
        render: (_, record) => record.origin && <ContractOriginTag origin={record.origin} />,
      },
      {
        key: "actions",
        align: "right",
        fixed: "right",
        width: 90,
        render: (_, record) => (
          <ActionTextIcon
            path={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.id })}
            icon="eye"
            color="blue"
            text={t("common.show")}
          />
        ),
      },
    ];

    return items;
  }, [contractListData, userAccount, hasCommissionsPermission]);

  return (
    <Card className="card-box">
      <Table<ContractList>
        {...tableStandardProps()}
        columns={columns}
        loading={isFetching}
        scroll={{ x: TableSizes.XXL }}
        dataSource={contractListData?.pageData}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(contractListData?.pageIndex) + 1,
          pageSize: contractListData?.pageSize,
          total: contractListData?.totalElementsCount,
        }}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, filters, sorter, updateSearchParams)}
        showSorterTooltip={false}
      />

      <ContractChangeVerificationStatusForm
        open={!!contractForVerificationForm}
        contract={contractForVerificationForm}
        viewType="sendToBackoffice"
        onFormCancel={() => setContractForVerificationForm(undefined)}
      />
    </Card>
  );
};
